// @flow

import _ from 'lodash';
import ko from 'rgs-knockout';
import Q from 'q';
import $ from 'rgs-jquery';
import { location } from 'window';
import url from 'url';

import page from 'page-common';

import onBeforeUnloadHandler from 'window-onbeforeunload-handler';

import { Error, ignoreHandledErrors } from 'error';

import { default as server, KnownErrorsHelper } from 'server2';

import { ObservableBusy } from 'observable-busy';

import { ContentCalcWorking as ContextBarContentCalcWorking } from 'context-bar';

import template from '../templates/template.html';

import * as templateThemes from '../templates/themes/*';

import { FeedbackModel } from './model.js';

import type { FeedbackModelOptions } from './model.js';

import type { default as Dictionaries } from './dictionaries';

import KbmOsagoPolicyMapper from './kbm-osago-policy-mapper';
import type { Captcha } from 'server2';

import submitAuthorizationErrorTemplate from '../templates/submit-auth-error-template.html';

const SUBMIT_ERROR_MESSAGE = `Произошла непредвиденная ошибка. Пожалуйста, попробуйте позднее`;
const formTypes = {
	DMS: 'dms',
};

export type ControllerOptions = {
	view: HTMLElement,
	context: Object,
	dictionaries: Dictionaries,
	config: Object,
}

export default class FeedbackController {
	_options: ControllerOptions;
	busy: ObservableBusy;
	_model: FeedbackModel;
	_contextBarContentWorking: any;

	constructor (options: ControllerOptions) {
		this._options = _.defaultsDeep({}, options, {
			view: undefined,
			context: {
				isEmployeeMode: false,
				isCategorySelectVisible: true,
				category: undefined,
				mode: undefined,
				key: undefined,
			},
			dictionaries: undefined,
		});

		this.busy = new ObservableBusy();

		const modelOptions: FeedbackModelOptions = {
			context: this._options.context,
			busy: this.busy,
			dictionaries: this._options.dictionaries,
			config: this._options.config,
		};

		this._model = new FeedbackModel(modelOptions);

		this._model.isCaptchaVisible.subscribe(async (isCaptchaVisible) => {
			if (!isCaptchaVisible) {
				return;
			}

			if (!this._model.Misc.Captcha.key()) {
				await this._generateCaptcha();
			}
		});

		this._model
			.on('submitClick', this._submit)
			.on('addPolicyClick', this._addPolicy)
			.on('removePolicyClick', this._removePolicy)
			.on('addDriverClick', this._addDriver)
			.on('removeDriverClick', this._removeDriver)
			.on('addCommentFileClick', this._addCommentFile);

		this._contextBarContentWorking = new ContextBarContentCalcWorking({
			name: 'calc-feedback-working',
			above: ['default'],
			visible: () => this.busy.isBusy(),
		});
		page.contextBar.add(this._contextBarContentWorking);

		onBeforeUnloadHandler.push(() => {
			return this._model.Misc.isKbmCheckFromLkMode()
				&& !this._model.Results.Success();
		});

		ko.utils.arrayForEach([
			this._model.CategoriesProvider.selectedService,
			this._model.CategoriesProvider.selectedCategory,
			this._model.CategoriesProvider.selectedSubcategory,
		], obs => {
			obs.subscribe(this._resetModelCategorySpecifiedFieldsToDefault);
		});

		this._checkDmsParams();

		ko.applyBindingsToNode(this._options.view, {
			template: {
				html: template({
					templates: {
						themes: templateThemes,
					},
				}),
				data: this._model,
			},
		});

		this._connectBeforeFormBanner();

		Q(this._start())
		.catch(ignoreHandledErrors)
		.done();
	}

	_connectBeforeFormBanner = () => {
		const beforeNoneEmployeeFormContent = $("#before-none-employee-form-container");

		if (beforeNoneEmployeeFormContent.length) {
			const beforeNoneEmployeeFormElement = $("#before-none-employee-form");
			beforeNoneEmployeeFormElement.html(beforeNoneEmployeeFormContent.html());
			beforeNoneEmployeeFormContent.empty();
		}
	};

	_start = async () => {
		this._setDefaultCategoryIfNeeded();

		// RGSSUP-16675 Проверяем:
		// 1. Запуск инициализирован переходом из ЛК
		// 2. Категория КБМ
		// 3. Пользователь не является сотрудником
		if (this._model.Misc.isKbmCheckFromLkMode() && !this._options.context.tokenError) {
			return await this._startKbmCheck();
		}
	};

	_startKbmCheck = async () => {
		this._model.PersonType.fromJS(this._options.dictionaries.personTypes.physical.id);

		return await this.busy.add(
			server.my.api.get([
				'complaint',
				'getOsagoPolicyData',
			], {
				query: {
					orderKey: this._options.context.key,
				},
			})
			.then(({ responseJSON }) => {
				this._model.Misc.KbmOsagoPolicyModel.fromJS(responseJSON.Data);

				const kbmOsagoPolicyMapper = new KbmOsagoPolicyMapper(
					this._model.Misc.KbmOsagoPolicyModel
				);
				kbmOsagoPolicyMapper.mapToKbmPolicy(this._model.KbmOsago.Policies()[0]);
			})
		);
	};


	_setDefaultCategoryIfNeeded = () => {
		const { CategoriesProvider } = this._model;
		const { categoryTypes } = this._options.dictionaries;

		const defaultCategory = CategoriesProvider.defaultCategory();
		if (!defaultCategory) {
			return;
		}

		CategoriesProvider.selectedTheme(defaultCategory.Theme);

		if (defaultCategory.Type === categoryTypes.service.id) {
			CategoriesProvider.selectedService(defaultCategory.ID);
		} else if (defaultCategory.Type === categoryTypes.category.id) {
			const service = CategoriesProvider.get(defaultCategory.ParentID);

			CategoriesProvider.selectedService(service.ID);
			CategoriesProvider.selectedCategory(defaultCategory.ID);
		} else if (defaultCategory.Type === categoryTypes.subcategory.id) {
			const category = CategoriesProvider.get(defaultCategory.ParentID);
			const service = CategoriesProvider.get(category.ParentID);

			CategoriesProvider.selectedService(service.ID);
			CategoriesProvider.selectedCategory(category.ID);
			CategoriesProvider.selectedSubcategory(defaultCategory.ID);
		}
	};

	_generateCaptcha = async () => {
		return await this.busy.add(
			server.my.cmsapi.get([
				'captcha',
				'generateKey',
			])
			.then(({ responseJSON }) => {
				this._model.Misc.Captcha.fromJS({ key: responseJSON });
				return responseJSON;
			})
		);
	};

	_getCaptcha = (): ?Captcha => {
		return !this._model.Misc.isEmployeeMode()
			? this._model.Misc.Captcha.toJS()
			: {};
	};

	_sendEmailCode = async () => {
		await this.busy.add(
			server.any.api.post([
				'keyCode',
				'createCode',
			], {
				body: {
					TypeGenerator: 'ComplaintsKeyCodeManager',
					ProductTypeId: 0,
					Key: this._model.xEmail(),
					Code: '',
				},
			})
			.then(({ responseJSON: { result } }) => {
				this._model.Misc.EmailCheck.isSent(result);
			})
		);
	};

	_checkEmailCode = async () => {
		return await this.busy.add(
			server.any.api.post([
				'keyCode',
				'checkCode',
			], {
				body: {
					TypeGenerator: 'ComplaintsKeyCodeManager',
					ProductTypeId: 0,
					Key: this._model.xEmail(),
					Code: this._model.Misc.EmailCheck.value(),
				},
			})
			.then(({ responseJSON: { result } }) => {
				if (result === false) {
					this._model.Misc.EmailCheck.value.setError('код указан неверно');
				} else {
					this._model.Misc.EmailCheck.value.clearError();
				}
				
				return result;
			})
		);
	};

	_submit = () => {
		Q(this._performChecksAndSubmit())
		.catch((e: Error) => {
			this._showErrorMessage(SUBMIT_ERROR_MESSAGE);
			e.handled = {
				type: 'notification',
				message: SUBMIT_ERROR_MESSAGE,
			 };
			 
			throw e;
		})
		.catch(ignoreHandledErrors)
		.done();
	};

	_performChecksAndSubmit = async () => {
		if (this._model.validationGroup().guard()) {
			return;
		}
		if (!this._model.Misc.EmailCheck.isCheckNotRequired()) {
			const isEmailCodeSent = this._model.Misc.EmailCheck.isSent();
			if (isEmailCodeSent !== true) {
				await this._sendEmailCode();
				return;
			}

			const isEmailCodeCorrect = await this._checkEmailCode();
			if (isEmailCodeCorrect !== true) {
				return;
			}
		}

		const captcha = this._getCaptcha();
		const params = this._model.toJS();
		this._checkDmsRequestParams(params);

		await this.busy.add(
			server.withCaptcha(captcha).my.api.post([
				'complaint',
				'submit',
			], {
				body: params,
			})
			.then(({ responseJSON }) => {
				this._model.Results.fromJS(responseJSON);
				if (!this._model.Results.Success()) {
					this._showErrorMessage(SUBMIT_ERROR_MESSAGE);
				}
			})
			.catch((e: Error) => {
				if (KnownErrorsHelper.isCaptchaValidationError(e)) {
					this._model.Misc.Captcha.value.setError('код указан неверно');
					e.handled = { type: 'silently' };
				}

				if (KnownErrorsHelper.isKbmSecurityError(e)) {
					page.notifications.push({
						css: { 'notification-danger': true },
						template: {
							html: submitAuthorizationErrorTemplate(),
							data: this._model,
						},
					});

					e.handled = { type: 'notification' };
				}

				throw e;
			})
			.catch(ignoreHandledErrors)
			.finally( async () => {
				await this._generateCaptcha();
			})
		);
	};

	_showErrorMessage = (message: string) => {
		page.notifications.push({
			css: { 'notification-danger': true },
			message,
		});
	};

	_addPolicy = () => {
		this._model.KbmOsago.addPolicy();
	};

	_removePolicy = (policy: mixed) => {
		this._model.KbmOsago.removePolicy(policy);
	};
	
	_addDriver = ({ policyId }: { policyId: string }) => {
		this._model.KbmOsago.getPolicy(policyId).addDriver();
	};

	_removeDriver = ({ policyId, driver }: { policyId: string, driver: mixed }) => {
		this._model.KbmOsago.getPolicy(policyId).removeDriver(driver);
	};

	_addCommentFile = () => {
		this._model.ContactInfo.addFile();
	};
 
	_resetModelCategorySpecifiedFieldsToDefault = () => {
		this._model.resetCategorySpecifiedFieldsToDefault();
	};

	_checkDmsParams = () => {
		const queryParams = url.parse(location.href, true).query;

		if (queryParams.formtype === formTypes.DMS) {
			this._model.PersonType(this._options.dictionaries.personTypes.physical.id);

			this._model.CategoriesProvider.selectedTheme(
				this._options.dictionaries.themes.health.id,
			);

			const selectedPolicyWay = _.find(
				this._model.CategoriesProvider.policiesWays(),
				item => item.Name === 'Предоставлен работодателем',
			);
			const selectedCategory = _.find(
				this._model.CategoriesProvider.currentThemeCategories(),
				item => item.Name === 'Вопрос по работе Личного кабинета ДМС',
			);
			this._model.CategoriesProvider.selectedPolicyWay(
				selectedPolicyWay.ID,
			);
			this._model.CategoriesProvider.selectedDmsCategory(
				selectedCategory.ID,
			);

			if (queryParams.ischild){
				const complaintText = `ФИО несовершеннолетнего: 
				${queryParams.lastname} ${queryParams.firstname} ${queryParams.middlename}, 
				Дата рождения: ${queryParams.birthday}`;
				this._model.ComplaintText(complaintText);
			} else {
				this._model.FirstName(
					queryParams.firstname,
				);
				this._model.LastName(
					queryParams.lastname,
				);
				this._model.MiddleName(
					queryParams.middlename,
				);
				this._model.Birthday(
					queryParams.birthday,
				);
				this._model.PolicyNumber(
					queryParams.policynumber,
				);
				this._model.FromDmsLk(true);
			}
		}
	};

	_checkDmsRequestParams = (params) => {
		if (this._model.CategoriesProvider.isThemeDms()) {
			params.ProductType = 'Dms';
		}
		if (this._model.CategoriesProvider.isThemeDms() && this._model.isDeclarerJurdicalPerson()) {
			params.Categories = [{
				ID: 'c3612008-f97c-4adb-8658-47e4fd673eff',
				Name: 'Добровольное медицинское страхование (ДМС)',
			}];
		} else if (this._model.CategoriesProvider.isThemeDms()) {
			params.Categories = [];
			params.Health = {
				PolicyType: this._model.CategoriesProvider.selectedPolicyWay(),
				ID: this._model.CategoriesProvider.selectedDmsCategory(),
			};
			const selectedCategory = _.find(
				this._model.CategoriesProvider.availableDmsCategories(),
				way => way.ID === this._model.CategoriesProvider.selectedDmsCategory(),
			);
			const selectedPolicyWay = _.find(
				this._model.CategoriesProvider.policiesWays(),
				way => way.ID === selectedCategory.ParentID,
			);
			const selectedTheme = _.find(
				this._model.CategoriesProvider.currentThemeCategories(),
				item => item.Type === 'Service' && item.ID === selectedPolicyWay.ParentID,
			);

			params.Categories.push({
				ID: selectedTheme.ID,
				Name: selectedTheme.Name,
			});
			params.Categories.push({
				ID: selectedCategory.ID,
				Name: selectedCategory.Name,
			});
		}
	};
}
